import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { FreeEventSummary } from '#/api/commerce';

import { fetchCommerceConfig } from '#/api/commerce';

/**
 * 커머스 설정 스토어 — 무료 개방 모드(029)의 <b>단일 판단 근거</b>(FR-003).
 *
 * 서버 `GET /api/fo/commerce-config` 값을 앱 부트 시 1회 로드해 보관한다. 랜딩 가격 섹션·장바구니 요약·
 * `/checkout` 진입 가드 3곳이 모두 이 값을 읽는다 — <b>화면별로 다시 판단하지 않는다</b>. 빌드타임 env 플래그를
 * 쓰지 않는 것도 같은 이유로, 서버 설정과 이원화되면 전환 시 한쪽만 바뀐다.
 *
 * <b>실패 시 유료(기본) 동작</b>: 설정을 못 받으면 `freeMode` 는 초기값 false 를 유지한다. 무료 표기가 안 뜨는
 * 것보다 결제 화면이 열린 채 결제가 불가능한 편이 나쁘지만, 반대로 설정 실패를 무료로 낙관하면 캠페인이 끝난 뒤에도
 * 무료 화면이 뜰 수 있다 — 보수적으로 기존 유료 흐름에 머무른다(에러 토스트는 http.ts 가 1회 표시).
 */
export const useCommerceStore = defineStore('commerce', () => {
  /** 무료 개방 활성 여부(서버 값). 미로드·로드 실패 시 false. */
  const freeMode = ref(false);

  /** 계정당 누적 수령 한도(0 = 무제한). 무료 모드가 아니면 0. */
  const claimLimit = ref(0);

  /**
   * 한시 무료 이벤트(050) 활성 여부(서버 값). 미로드·로드 실패 시 false — 실패를 이벤트 중으로
   * 낙관하면 종료 후에도 배너·무료 CTA 가 떠서 발급 404 로 이어진다(freeMode 와 동일한 보수 폴백).
   */
  const reviewEvent = ref(false);

  /**
   * 상품군 무료 이벤트(081) 공개 상태(서버 값). 미로드·로드 실패 시 빈 목록 — 이벤트 닫힘으로 취급한다
   * (reviewEvent 와 동일한 보수 폴백: 실패를 개방으로 낙관하면 종료 후에도 배너가 뜬다).
   */
  const freeEvents = ref<FreeEventSummary[]>([]);

  /** 설정 로드 완료 여부 — 가드가 판정 전 로드를 보장하는 데 쓴다(중복 호출 방지). */
  const configLoaded = ref(false);

  /** 동시 진입 시 중복 요청을 막기 위한 공유 Promise. */
  let loadPromise: Promise<void> | null = null;

  /** 마지막 서버 조회 시각(ms) — 재검증 스로틀 기준. */
  let lastFetchedAt = 0;

  /** 재검증 최소 간격 — 내비게이션마다 서버를 두드리지 않기 위한 스로틀(1분). */
  const REVALIDATE_MIN_INTERVAL_MS = 60_000;

  /** 서버 값 반영 — loadConfig/revalidate 공용(반영 지점 단일화). */
  function applyConfig(config: {
    freeMode: boolean;
    claimLimit: number;
    reviewEvent: boolean;
    freeEvents?: FreeEventSummary[];
  }): void {
    freeMode.value = config.freeMode;
    claimLimit.value = config.claimLimit;
    reviewEvent.value = config.reviewEvent;
    freeEvents.value = config.freeEvents ?? [];
    lastFetchedAt = Date.now();
  }

  /**
   * 서버 설정을 1회 로드한다. 이미 로드됐으면 즉시 반환하고, 진행 중이면 같은 Promise 를 공유한다.
   *
   * 라우터 가드가 `/checkout` 차단을 판정하기 전에 await 해야 한다 — 미도착 상태로 판정하면 무료 기간에도
   * 결제 화면이 한 번 열린다(R7).
   */
  async function loadConfig(): Promise<void> {
    if (configLoaded.value) return;
    if (loadPromise) return loadPromise;
    loadPromise = (async () => {
      try {
        applyConfig(await fetchCommerceConfig());
      } finally {
        // 실패해도 완료로 표시한다 — 매 내비게이션마다 재시도하면 가드가 그때마다 네트워크를 기다린다.
        configLoaded.value = true;
      }
    })();
    try {
      await loadPromise;
    } finally {
      loadPromise = null;
    }
  }

  /**
   * 백그라운드 재검증(050 코드리뷰 P1) — 이벤트 활성 여부가 <b>시각 경계로 자동 전환</b>되므로 부트 1회
   * 로드만으로는 경계를 넘긴 열린 탭이 낡은 값을 계속 쓴다(8/24 전에 연 탭은 시작 후에도 배너가 없고,
   * 마감 전에 연 탭은 종료 후에도 무료 CTA 가 남아 발급 400 으로 이어진다).
   *
   * 라우트 이동 시 fire-and-forget 으로 호출한다 — 내비게이션을 막지 않고(await 없음), 1분 스로틀로
   * 서버 부하를 억제한다. 실패는 무시(기존 값 유지 — 다음 이동에서 재시도).
   */
  function revalidate(): void {
    if (!configLoaded.value || loadPromise) return; // 최초 로드는 loadConfig 책임
    if (Date.now() - lastFetchedAt < REVALIDATE_MIN_INTERVAL_MS) return;
    lastFetchedAt = Date.now(); // 선점 — 연속 내비게이션의 중복 요청 방지
    fetchCommerceConfig({ silent: true })
      .then(applyConfig)
      .catch(() => {
        // 조용히 무시 — 값은 기존 것을 유지하고 다음 재검증 기회에 다시 시도한다.
      });
  }

  /** 상품군 무료 이벤트가 지금 열려 있는지(081) — 배너·상품 띠·안내 화면 분기의 단일 근거. */
  function isFreeEventOpen(groupCode: string): boolean {
    return freeEvents.value.some((e) => e.groupCode === groupCode && e.open);
  }

  /** 상품군 무료 이벤트 종료 경계(ISO, 없으면 null). */
  function freeEventEndsAt(groupCode: string): string | null {
    return freeEvents.value.find((e) => e.groupCode === groupCode)?.endsAt ?? null;
  }

  return {
    freeMode,
    claimLimit,
    reviewEvent,
    freeEvents,
    configLoaded,
    loadConfig,
    revalidate,
    isFreeEventOpen,
    freeEventEndsAt,
  };
});
